import type {
  DashboardPeriod,
  OrganizationManagerDashboardStatsDto,
  RepairQuarterCode,
} from './organization-manager-dashboard.types';

const PERIOD_TO_QUARTER: Partial<Record<DashboardPeriod, RepairQuarterCode>> = {
  'q1-2026': 'Q1',
  'q2-2026': 'Q2',
  'q3-2026': 'Q3',
  'q4-2026': 'Q4',
};

/** Deterministic KPI deltas so changing the period is visibly reflected in the mock UI. */
const PERIOD_SESSION_RATE: Record<DashboardPeriod, number> = {
  'q1-2026': 91,
  'q2-2026': 93,
  'q3-2026': 94,
  'q4-2026': 95,
  'last-12-months': 93,
};

const PERIOD_BREACHES: Record<DashboardPeriod, number> = {
  'q1-2026': 3,
  'q2-2026': 2,
  'q3-2026': 2,
  'q4-2026': 1,
  'last-12-months': 4,
};

export function quarterCodeFromPeriod(
  period: DashboardPeriod,
): RepairQuarterCode | null {
  return PERIOD_TO_QUARTER[period] ?? null;
}

export function defaultDashboardPeriod(
  now: Date = new Date(),
): DashboardPeriod {
  const year = now.getFullYear();
  if (year !== 2026) {
    return 'last-12-months';
  }
  const month = now.getMonth(); // 0-based
  if (month <= 2) return 'q1-2026';
  if (month <= 5) return 'q2-2026';
  if (month <= 8) return 'q3-2026';
  return 'q4-2026';
}

/**
 * Shapes the base mock (or future API payload) for the selected reporting window.
 * Keeps aggregation rules out of React components.
 */
export function applyDashboardPeriod(
  stats: OrganizationManagerDashboardStatsDto,
  period: DashboardPeriod,
): OrganizationManagerDashboardStatsDto {
  const selectedQuarter = quarterCodeFromPeriod(period);

  return {
    ...stats,
    sessionSuccess: {
      ...stats.sessionSuccess,
      ratePercent: PERIOD_SESSION_RATE[period],
    },
    accountability: {
      ...stats.accountability,
      activeBreaches: PERIOD_BREACHES[period],
    },
    repairHours: {
      ...stats.repairHours,
      currentHours: selectedQuarter
        ? (stats.repairHours.quarters.find((q) => q.quarter === selectedQuarter)
            ?.hours ?? stats.repairHours.currentHours)
        : stats.repairHours.currentHours,
      quarters: stats.repairHours.quarters.map((quarter) => ({
        ...quarter,
        highlighted:
          selectedQuarter != null ? quarter.quarter === selectedQuarter : false,
      })),
    },
  };
}
